// Region-aware date formatting for every user-facing date in the app.
//
// The active locale is `<ui language>-<organization country>` (e.g. `tr-TR`,
// `en-TR`); dates render numerically in the country's field order and
// separators (Turkey → 18.08.2018, US → 08/18/2018). Layout calls
// setDateRegion() during render whenever the tenant config or UI language
// changes; until then formatters fall back to the browser locale.
//
// These helpers are for DISPLAY only — machine formats (API payloads,
// <input type="date"> values, CSV exports) must keep using ISO yyyy-MM-dd.

import { formatDistanceToNow } from 'date-fns';
import { tr as dfnsTr } from 'date-fns/locale';

let currentLocale; // BCP-47 tag, undefined = browser default
let currentLang;
let currentCountry;
const formatters = new Map();

/**
 * Set the display locale. Either argument may be omitted to keep the current
 * value — the UI language and the organization's country arrive from different
 * places (i18n vs. tenant config) and must not clear each other.
 */
export function setDateRegion(language, country) {
  if (language) currentLang = String(language).split('-')[0];
  if (country !== undefined) currentCountry = country;
  const lang = currentLang || 'en';
  const next = currentCountry ? `${lang}-${currentCountry}` : lang;
  if (next !== currentLocale) {
    currentLocale = next;
    formatters.clear();
  }
}

// Current resolved locale tag for components that build their own
// Intl formatters (e.g. the DateField calendar). Undefined = browser default.
export function getDateLocale() {
  return currentLocale;
}

function getFormatter(opts) {
  const key = JSON.stringify(opts);
  let f = formatters.get(key);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat(currentLocale, opts);
    } catch (_e) {
      f = new Intl.DateTimeFormat(undefined, opts);
    }
    formatters.set(key, f);
  }
  return f;
}

function toDate(value) {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function run(value, opts, fallback) {
  const d = toDate(value);
  return d ? getFormatter(opts).format(d) : fallback;
}

// All date displays use "hard" numeric dates in the region's field order and
// separators — 18.08.2018 (TR), 08/18/2018 (US) — never month names.
const NUMERIC_DATE = { day: '2-digit', month: '2-digit', year: 'numeric' };

export function formatDate(value, fallback = '—') {
  return run(value, NUMERIC_DATE, fallback);
}

// Kept as separate entry points (former month-name variants) so call sites
// can stay expressive; all resolve to the numeric regional format.
export function formatDateMedium(value, fallback = '—') {
  return run(value, NUMERIC_DATE, fallback);
}

export function formatDateLong(value, fallback = '—') {
  return run(value, NUMERIC_DATE, fallback);
}

// Numeric date + time, e.g. 18.08.2018 14:30
export function formatDateTime(value, fallback = '—') {
  return run(value, { ...NUMERIC_DATE, hour: '2-digit', minute: '2-digit' }, fallback);
}

// Numeric date + time with seconds (audit trails, tooltips)
export function formatDateTimeFull(value, fallback = '—') {
  return run(value, { ...NUMERIC_DATE, hour: '2-digit', minute: '2-digit', second: '2-digit' }, fallback);
}

// 18.08 / 08/18 — compact, year implied
export function formatMonthDay(value, fallback = '—') {
  return run(value, { day: '2-digit', month: '2-digit' }, fallback);
}

// 18.08 14:30 — compact date + time
export function formatMonthDayTime(value, fallback = '—') {
  return run(value, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }, fallback);
}

// 14:30 or 2:30 PM — hour cycle follows the region
export function formatTime(value, fallback = '—') {
  return run(value, { hour: '2-digit', minute: '2-digit' }, fallback);
}

// "Wednesday, August 18" — dashboard greeting
export function formatWeekdayDate(value, fallback = '—') {
  return run(value, { weekday: 'long', month: 'long', day: 'numeric' }, fallback);
}

const dfnsLocale = () =>
  ((currentLocale || '').split('-')[0] === 'tr' ? dfnsTr : undefined);

// "3 minutes ago" / "3 dakika önce" — follows the UI language
export function formatRelativeTime(value, fallback = '—') {
  const d = toDate(value);
  if (!d) return fallback;
  return formatDistanceToNow(d, { addSuffix: true, locale: dfnsLocale() });
}

// Bare distance, no "ago"/"in" suffix: "about 24 hours" / "yaklaşık 24 saat".
// For call sites that put the duration inside their own translated sentence
// (e.g. "Overdue by {{time}}") — those must not hard-code English by calling
// date-fns directly, or the sentence comes out half-translated.
export function formatTimeDistance(value, fallback = '—') {
  const d = toDate(value);
  if (!d) return fallback;
  return formatDistanceToNow(d, { locale: dfnsLocale() });
}

// Same value with the first letter capitalized, for languages whose sentence
// starts with the duration ("Yaklaşık 24 saat gecikmiş").
export function formatTimeDistanceCapitalized(value, fallback = '—') {
  const s = formatTimeDistance(value, fallback);
  return s ? s.charAt(0).toLocaleUpperCase(currentLocale || undefined) + s.slice(1) : s;
}
